import { EntityToUpdate } from "abstract/entity-to-update"
import { Contest } from "../store/contests.store"
import { apiCall } from "api-calls/api-call"

export const getContests = async(
  filter: string,
  page: number,
  limit: number,
): Promise<{
  data: Contest[];
  total: number;
}> => {
  const query: { page: number; limit: number; filter?: string } = {
    page,
    limit,
  }

  if (filter) query.filter = filter

  return apiCall({
    method: "GET",
    path: "contests",
    query: query,
  })
}

export const updateContest = async(
  id: string,
  data: EntityToUpdate<Contest>,
): Promise<{
  data: Contest;
}> => {
  return apiCall({
    method: "PUT",
    path: `contests/${id}`,
    body: data,
  })
}

export const createContest = async(data: EntityToUpdate<Contest>): Promise<{
  data: Contest;
}> => {
  return apiCall({
    method: "POST",
    path: "contests",
    body: data,
  })
}

export const deleteContest = async(id: string): Promise<{
  data: Contest;
}> => {
  return apiCall({
    method: "DELETE",
    path: `contests/${id}`,
  })
}

export interface ContestPeriod {
  id: string;
  startsAt: string;
  endsAt: string;
  contestId: string;
  status: "ENDED" | "ACTIVE" | "UPCOMING";
}

export const getContestPeriods = async(contestName: string): Promise<ContestPeriod[]> => {
  return apiCall({
    method: "GET",
    path: `challenge-based-contests/contests/${contestName}/contest-periods`,
  })
}

export const deleteContestPeriod = async(contestPeriodId: string): Promise<void> => {
  return apiCall({
    method: "DELETE",
    path: `challenge-based-contests/contest-periods/${contestPeriodId}`,
  })
}

export interface CreateContestPeriodRequest {
  startsAt: string;
  endsAt: string;
}

export interface ContestPeriodGame {
  createdAt: string;
  contestPeriodId: string;
  gameId: number;
  prizesCount: number;
  prizeBuffPoints: number;
  prizeGiftCard: string | null;
  raffleId?: string;
}

export interface GameChallenge {
  id: string;
  createdAt: string;
  updatedAt: string;
  title: string;
  goal: number;
  type: "NOT_ACCUMULATIVE" | "ACCUMULATIVE";
  actionText: string | null;
  identifier: string;
  targetValue: number | null;
  gameId: number;
  contestPeriodId: string;
  resetPeriod: string | null;
}

export const createContestPeriod = async(contestName: string, data: CreateContestPeriodRequest): Promise<ContestPeriod> => {
  return apiCall({
    method: "POST",
    path: `challenge-based-contests/contests/${contestName}/contest-periods`,
    body: data,
  })
}

export const updateContestPeriod = async(contestPeriodId: string, data: CreateContestPeriodRequest): Promise<ContestPeriod> => {
  return apiCall({
    method: "PUT",
    path: `challenge-based-contests/contest-periods/${contestPeriodId}`,
    body: data,
  })
}

export const getContestPeriodGames = async(contestPeriodId: string): Promise<ContestPeriodGame[]> => {
  return apiCall({
    method: "GET",
    path: `challenge-based-contests/contest-periods/${contestPeriodId}/games`,
  })
}

export const getGameChallenges = async(contestPeriodId: string, gameId: number): Promise<GameChallenge[]> => {
  return apiCall({
    method: "GET",
    path: `challenge-based-contests/contest-periods/${contestPeriodId}/games/${gameId}/challenges`,
  })
}

export interface CreateContestPeriodGameRequest {
  gameId: number;
  prizeBuffPoints: number;
  prizeGiftCard: string | null;
  prizesCount: number;
  raffleId?: string;
}

export interface UpdateContestPeriodGameRequest {
  prizeBuffPoints: number;
  prizeGiftCard: string | null;
  prizesCount: number;
  raffleId?: string;
}

export const createContestPeriodGame = async(
  contestPeriodId: string,
  data: CreateContestPeriodGameRequest,
): Promise<ContestPeriodGame> => {
  return apiCall({
    method: "POST",
    path: `challenge-based-contests/contest-periods/${contestPeriodId}/games`,
    body: data,
  })
}

export const updateContestPeriodGame = async(
  contestPeriodId: string,
  gameId: number,
  data: UpdateContestPeriodGameRequest,
): Promise<ContestPeriodGame> => {
  return apiCall({
    method: "PUT",
    path: `challenge-based-contests/contest-periods/${contestPeriodId}/games/${gameId}`,
    body: data,
  })
}

export const deleteContestPeriodGame = async(
  contestPeriodId: string,
  gameId: number,
): Promise<void> => {
  return apiCall({
    method: "DELETE",
    path: `challenge-based-contests/contest-periods/${contestPeriodId}/games/${gameId}`,
  })
}

export interface CreateGameChallengeRequest {
  title: string;
  actionText: string;
  type: "ACCUMULATIVE" | "NOT_ACCUMULATIVE";
  identifier: string;
  targetValue: number;
  goal: number;
}

export interface UpdateGameChallengeRequest {
  title: string;
  actionText: string;
  type: "ACCUMULATIVE" | "NOT_ACCUMULATIVE";
  identifier: string;
  targetValue: number;
  goal: number;
}

export const createGameChallenge = async(
  contestPeriodId: string,
  gameId: number,
  data: CreateGameChallengeRequest,
): Promise<GameChallenge> => {
  return apiCall({
    method: "POST",
    path: `challenge-based-contests/contest-periods/${contestPeriodId}/games/${gameId}/challenges`,
    body: data,
  })
}

export const updateGameChallenge = async(
  challengeId: string,
  data: UpdateGameChallengeRequest,
): Promise<GameChallenge> => {
  return apiCall({
    method: "PUT",
    path: `challenge-based-contests/${challengeId}`,
    body: data,
  })
}

export const deleteGameChallenge = async(challengeId: string): Promise<void> => {
  return apiCall({
    method: "DELETE",
    path: `challenge-based-contests/${challengeId}`,
  })
}
